import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTasks } from '../../store/slices/taskSlice';
import { addNotification } from '../../store/slices/uiSlice';

const TaskMappingDialog = ({ isOpen, onClose, onTaskSelected }) => {
  const dispatch = useDispatch();
  const { tasks, loading } = useSelector((state) => state.tasks);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [expandedTaskIds, setExpandedTaskIds] = useState(new Set());

  useEffect(() => {
    if (isOpen) {
      // Fetch all tasks when dialog opens
      dispatch(fetchTasks());
      setSearchQuery('');
      setSelectedTaskId(null);
      setExpandedTaskIds(new Set());
    }
  }, [isOpen, dispatch]);

  const toggleTaskDescription = (taskId, e) => {
    e.stopPropagation();
    setExpandedTaskIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const filteredTasks = tasks
    .filter((task) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      const title = (task.title || '').toLowerCase();
      const description = task.description || '';
      // Strip HTML tags for search
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = description;
      const plainDescription = (tempDiv.textContent || tempDiv.innerText || '').toLowerCase();
      return title.includes(query) || plainDescription.includes(query);
    })
    .sort((a, b) => {
      // Sort order: PENDING, PICK_TODAY, IN_PROGRESS, CANCELLED, COMPLETED
      const statusOrder = {
        'PENDING': 1,
        'PICK_TODAY': 2,
        'IN_PROGRESS': 3,
        'CANCELLED': 4,
        'COMPLETED': 5
      };

      const orderA = statusOrder[a.status] || 6;
      const orderB = statusOrder[b.status] || 6;

      return orderA - orderB;
    });

  const handleSelect = () => {
    if (selectedTaskId) {
      const selectedTask = tasks.find(t => t.id === selectedTaskId);
      if (selectedTask) {
        onTaskSelected(selectedTask);
        onClose();
      }
    } else {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Please select a task to map to.',
        })
      );
    }
  };

  const handleCancel = () => {
    setSelectedTaskId(null);
    setSearchQuery('');
    onClose();
  };

  // Helper to get initial words from description
  const getInitialWordsFromDescription = (description, maxWords = 8) => {
    if (!description) return 'No description';

    let text = '';

    // Check if description is HTML
    if (description.trim().startsWith('<') && description.includes('</')) {
      // Strip HTML tags
      const tmp = document.createElement('div');
      tmp.innerHTML = description;
      text = tmp.textContent || tmp.innerText || '';
    } else {
      text = description;
    }

    // Clean up whitespace and get words
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);

    if (words.length === 0) return 'No description';

    // Take first maxWords words
    const initialWords = words.slice(0, maxWords).join(' ');

    // Add ellipsis if there are more words
    return words.length > maxWords ? initialWords + '...' : initialWords;
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleCancel}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 text-primary-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Map to an Existing Task
              </h3>
            </div>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-6">
            <div className="space-y-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Search Tasks
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Search by title or description..."
                />
              </div>

              {/* Task List */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select a task to map to:
                </label>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading tasks...</p>
                  </div>
                ) : filteredTasks.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {searchQuery ? 'No tasks found matching your search.' : 'No tasks available.'}
                  </div>
                ) : (
                  <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg divide-y divide-gray-200 dark:divide-gray-600">
                    {filteredTasks.map((task) => {
                      const isExpanded = expandedTaskIds.has(task.id);
                      const isSelected = selectedTaskId === task.id;
                      const taskTitle = task.title || getInitialWordsFromDescription(task.description, 8);

                      return (
                        <div
                          key={task.id}
                          className={`${isSelected
                            ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-l-primary-600'
                            : 'bg-white dark:bg-gray-800'
                            } hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer`}
                          onClick={() => setSelectedTaskId(task.id)}
                        >
                          {/* Main row: Title on left, Status on right */}
                          <div className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {/* Selection indicator */}
                              <div className="flex-shrink-0">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected
                                    ? 'border-primary-600 bg-primary-600'
                                    : 'border-gray-300 dark:border-gray-600'
                                  }`}>
                                  {isSelected && (
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              </div>

                              {/* Title - truncated to one line */}
                              <h4
                                className="text-sm font-semibold text-gray-900 dark:text-white flex-1 min-w-0 truncate"
                                title={taskTitle}
                              >
                                {taskTitle}
                              </h4>
                            </div>

                            {/* Status badge on right */}
                            <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${task.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                  task.status === 'PICK_TODAY' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200' :
                                  task.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                                    task.status === 'COMPLETED' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                      task.status === 'CANCELLED' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                        'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                }`}>
                                {task.status === 'PICK_TODAY' ? 'Pick today' : task.status === 'IN_PROGRESS' ? 'In Progress' : task.status === 'PENDING' ? 'Pending' : task.status === 'COMPLETED' ? 'Completed' : task.status === 'CANCELLED' ? 'Cancelled' : task.status}
                              </span>

                              {/* Expand/collapse button for description */}
                              {task.description && (
                                <button
                                  onClick={(e) => toggleTaskDescription(task.id, e)}
                                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex-shrink-0"
                                  aria-label={isExpanded ? 'Collapse description' : 'Expand description'}
                                >
                                  <svg
                                    className={`w-5 h-5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Accordion: Description (collapsible) */}
                          {task.description && isExpanded && (
                            <div className="px-4 pb-4 pt-0 border-t border-gray-200 dark:border-gray-700 mt-0">
                              <div className="pt-4 text-sm text-gray-600 dark:text-gray-400">
                                {task.description && task.description.trim().startsWith('<') && task.description.includes('</') ? (
                                  <div
                                    className="ql-editor-content"
                                    dangerouslySetInnerHTML={{ __html: task.description }}
                                    style={{
                                      fontSize: '0.875rem',
                                      lineHeight: '1.5',
                                    }}
                                  />
                                ) : (
                                  <p className="whitespace-pre-wrap">{task.description || 'No description'}</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedTaskId}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Select Task
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TaskMappingDialog;

